import React, { useContext, useEffect, useState } from 'react';
import { UserContext } from '../UserContext';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../ApiContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAt } from '@fortawesome/free-solid-svg-icons';
import FormActionButton from '../components/FormActionButton';
import Container from '../components/Container';
import Heading2 from '../components/Heading2';
import { ApiErrorAlert, ApiError } from '../components/ApiErrorAlert';

const EnterEmailScreen: React.FC = () => {
  const userContext = useContext(UserContext);
  const api = useApi();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<ApiError | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (userContext) {
      const { email } = userContext.userData;
      setEmail(email || '');
    }
  }, [userContext]);

  if (!userContext) return null;

  const { userData, setUserData } = userContext;

  const providerId = userData.provider_id;
  if (!providerId) {
    // Invalid state, return to beginning
    navigate('/');
    return null;
  }

  const handleFormSubmit = async () => {
    setUserData({ ...userData, email });
    setIsLoading(true);

    const { data: customerExistsData, error: customerExistsError } = await api.client.doesCustomerExist(email);

    if (customerExistsError) {
      setApiError(customerExistsError);
      setIsLoading(false);
      return;
    }

    const customerId = customerExistsData?.customer_id;

    if (customerId) {
      setUserData({ ...userData, email, customer_id: customerId });
      const { data: hasCardData, error: hasCardError } = await api.client.doesCustomerHaveCard(customerId, providerId);

      if (hasCardError) {
        setApiError(hasCardError);
        setIsLoading(false);
        return;
      }

      if (!hasCardData?.hasCard) {
        // They don’t have a card, go to set up their payment card.
        navigate('/billing-address');
      } else {
        // Optionally we could test if they have a home address, and send them to create it if missing.
        navigate('/confirm-appointment');
      }
    } else {
      // Customer does not exist, so start by creating one.
      navigate('/create-password');
    }

    setIsLoading(false);
  };

  return (
    <Container>
      <Heading2>What's Your Email Address?</Heading2>
      <p>
        If this is your first time booking with a Skin Clique provider, we'll walk you through setting up your account
        so you can complete your forms online and manage your appointments.
      </p>
      <div className="mb-6 relative">
        <div className="flex items-center w-full border-b-2 border-black py-[.5rem]">
          <span className="inset-y-0 left-0 flex items-center mr-2 text-gray-500">
            <FontAwesomeIcon icon={faAt} className="h-5 w-5" />
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-grow w-full appearance-none text-gray-700 p-2"
            placeholder="EMAIL ADDRESS"
          />
        </div>
      </div>
      <div className="flex justify-center mt-8">
        <FormActionButton onClick={handleFormSubmit} isLoading={isLoading}>
          Sign In / Create Account
        </FormActionButton>
      </div>
      <ApiErrorAlert screenName="enter email screen" apiError={apiError} clearError={setApiError} />
    </Container>
  );
};

export default EnterEmailScreen;
